import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';

import { documentTypeIcon } from '~/components/DocumentsPage/Document.utils';
import { useRouting } from '~/routing/useRouting';
import Box from '~/ui/components/Box/Box';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import Navigator from '~/ui/components/Navigator/Navigator';

const getNavigatorTooltip = (item) => {
  if (!item) return null;

  const { fileType, name } = item;
  const iconSize = '20';
  const icon = documentTypeIcon(fileType, iconSize);

  return (
    <FlexLayout alignItems="center" space={3}>
      <FlexLayout flex="none">{icon}</FlexLayout>
      <Box>{name}</Box>
    </FlexLayout>
  );
};

const DocumentIdWatcher = ({ documentId, setIndexById, verifyId }) => {
  useEffect(() => {
    setIndexById({ value: documentId });
    verifyId(documentId);
  }, [documentId, verifyId, setIndexById]);

  return null;
};

const DocumentsNavigation = forwardRef(
  (
    {
      context,
      documentId,
      fillCurrentIndexList,
      verifyHandlerId,
      location,
      selectItem,
      setIndexById,
    },
    ref,
  ) => {
    const { navigate } = useRouting();

    const navigateToDocument = (docId) => {
      const { search } = location;
      const newUrl = `/document/${docId}${search}`;
      navigate(newUrl);
    };

    const tryNavigateWithLocalList = (index, list) => {
      const item = list[index];
      if (item) {
        navigateToDocument(item.id);
      }
    };

    const tryNavigateTo = (offset) => {
      const item = selectItem(context, offset);
      if (item) {
        navigateToDocument(item.id);
      }
    };

    const handleChange = async (value) => {
      const { currentIndex, total } = context;

      if (value > total) return;

      const index = value - 1;

      if (index === currentIndex) return;

      await fillCurrentIndexList({
        context,
        index,
        callback: (list) => {
          tryNavigateWithLocalList(index, list);
        },
      });
    };

    const handlePrev = async () => {
      const index = context.currentIndex - 1;
      await fillCurrentIndexList({
        context,
        index,
      });
      tryNavigateTo(-1);
    };

    const handleNext = async () => {
      const index = context.currentIndex + 1;
      await fillCurrentIndexList({
        context,
        index,
      });
      tryNavigateTo(+1);
    };

    useImperativeHandle(ref, () => ({
      handleNext,
      handlePrev,
    }));

    const handleVerifyId = useCallback(
      async (handlerId) => {
        if (context.isInitialized && context.getPosition) {
          await verifyHandlerId({
            context,
            handlerId,
          });
        }
      },
      [context.isInitialized, context.getPosition, verifyHandlerId],
    );

    const handleClick = () => {
      navigate(context.fromLocation ?? -1);
      context.onClick?.();
    };

    const shouldShowNavigator =
      context.isInitialized &&
      context.currentIndex >= 0 &&
      !context.fetching &&
      context.total > 0;

    return (
      <>
        {shouldShowNavigator && (
          <Navigator
            label={context.name}
            onClick={handleClick}
            onChange={handleChange}
            onNext={handleNext}
            onPrev={handlePrev}
            tooltipPrev={getNavigatorTooltip(selectItem(context, -1))}
            tooltipNext={getNavigatorTooltip(selectItem(context, +1))}
            total={context.total}
            value={1 + context.currentIndex}
          />
        )}
        <DocumentIdWatcher
          documentId={documentId}
          setIndexById={setIndexById}
          verifyId={handleVerifyId}
        />
      </>
    );
  },
);

export default DocumentsNavigation;
